import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DocumentTextIcon,
  EnvelopeIcon,
  PencilIcon,
  PhoneIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { toastError, toastInfo, toastSuccess } from "../utils/toast";

const emptyForm = {
  nome: "",
  cognome: "",
  telefono: "",
  email: "",
  note: "",
};

const ContattoResponsabile = () => {
  const { user, activeCongregazione } = useAuth();
  const [contatto, setContatto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const canManage = user?.ruolo === "admin" || user?.ruolo === "super_admin";
  const requiresCongregazioneSelection =
    user?.ruolo === "super_admin" && !activeCongregazione?.id;

  const congregazioneParams = useMemo(() => {
    if (user?.ruolo === "super_admin" && activeCongregazione?.id) {
      return { congregazione_id: activeCongregazione.id };
    }
    return undefined;
  }, [user?.ruolo, activeCongregazione?.id]);

  const loadContatto = useCallback(async () => {
    if (requiresCongregazioneSelection) {
      setContatto(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get("/contatto-responsabile", {
        params: congregazioneParams,
      });
      setContatto(response.data?.contatto || null);
    } catch (error) {
      console.error("Errore nel caricamento del contatto responsabile:", error);
      toastError("Errore nel caricamento del contatto responsabile");
    } finally {
      setLoading(false);
    }
  }, [requiresCongregazioneSelection, congregazioneParams]);

  useEffect(() => {
    loadContatto();
  }, [loadContatto]);

  useEffect(() => {
    if (requiresCongregazioneSelection) {
      toastInfo("Seleziona una congregazione attiva per vedere il contatto");
    }
  }, [requiresCongregazioneSelection]);

  const openCreateModal = () => {
    setForm(emptyForm);
    setIsEditing(false);
    setShowModal(true);
  };

  const openEditModal = () => {
    if (!contatto) return;
    setForm({
      nome: contatto.nome || "",
      cognome: contatto.cognome || "",
      telefono: contatto.telefono || "",
      email: contatto.email || "",
      note: contatto.note || "",
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setShowModal(false);
  };

  const buildPayload = () => ({
    nome: form.nome.trim(),
    cognome: form.cognome.trim(),
    telefono: form.telefono.trim(),
    email: form.email.trim(),
    note: form.note.trim(),
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const payload = buildPayload();
    if (!payload.nome || !payload.cognome) {
      toastError("Nome e cognome sono obbligatori");
      return;
    }

    setSubmitting(true);
    try {
      const response = isEditing
        ? await api.put(`/contatto-responsabile/${contatto.id}`, payload)
        : await api.post("/contatto-responsabile", payload, {
            params: congregazioneParams,
          });

      const updated = response.data?.contatto;
      if (updated) {
        setContatto(updated);
      } else {
        await loadContatto();
      }
      toastSuccess(
        isEditing
          ? "Contatto aggiornato con successo"
          : "Contatto aggiunto con successo"
      );
      setShowModal(false);
    } catch (error) {
      console.error("Errore nel salvataggio del contatto:", error);
      toastError(
        error.response?.data?.message ||
          "Errore nel salvataggio del contatto responsabile"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!contatto || deleting) return;
    const confirmed = window.confirm(
      "Sei sicuro di voler eliminare il contatto del responsabile?"
    );
    if (!confirmed) return;

    setDeleting(true);
    try {
      await api.delete(`/contatto-responsabile/${contatto.id}`);
      setContatto(null);
      toastSuccess("Contatto eliminato con successo");
    } catch (error) {
      console.error("Errore nell'eliminazione del contatto:", error);
      toastError(
        error.response?.data?.message ||
          "Errore nell'eliminazione del contatto responsabile"
      );
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento contatto...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Contatto Responsabile
              </h1>
              <p className="mt-2 text-gray-600">
                Informazioni di contatto del responsabile della congregazione
              </p>
              <div className="accent-line mt-4" />
            </div>
            {canManage && !contatto && (
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Aggiungi
              </button>
            )}
          </div>
          {requiresCongregazioneSelection && (
            <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Seleziona una congregazione attiva per visualizzare o gestire il
              contatto.
            </div>
          )}
        </div>

        {!requiresCongregazioneSelection && !contatto && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center relative overflow-hidden">
            <span className="accent-bar top-8 bottom-8" aria-hidden="true" />
            <UserIcon className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Nessun contatto disponibile
            </h2>
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              I dati del responsabile non sono ancora stati inseriti.
            </p>
            {canManage && (
              <button
                type="button"
                onClick={openCreateModal}
                className="mt-5 inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Aggiungi contatto
              </button>
            )}
          </div>
        )}

        {!requiresCongregazioneSelection && contatto && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 relative overflow-hidden">
            <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
                  <UserIcon className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {contatto.nome} {contatto.cognome}
                  </h2>
                  <p className="text-sm text-gray-500">
                    Responsabile della congregazione
                  </p>
                </div>
              </div>
              {canManage && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={openEditModal}
                    className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Modifica
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="inline-flex items-center px-3 py-1.5 rounded-md border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-60"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    {deleting ? "Eliminazione..." : "Elimina"}
                  </button>
                </div>
              )}
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <PhoneIcon className="h-5 w-5 text-gray-400" />
                {contatto.telefono ? (
                  <a
                    href={`tel:${contatto.telefono}`}
                    className="hover:text-primary-600"
                  >
                    {contatto.telefono}
                  </a>
                ) : (
                  <span className="text-gray-500">Telefono non disponibile</span>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                {contatto.email ? (
                  <a
                    href={`mailto:${contatto.email}`}
                    className="hover:text-primary-600"
                  >
                    {contatto.email}
                  </a>
                ) : (
                  <span className="text-gray-500">Email non disponibile</span>
                )}
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                Note
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 whitespace-pre-line">
                {contatto.note || "Nessuna nota disponibile."}
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <p className="text-xs uppercase text-gray-500">
                  {isEditing ? "Modifica contatto" : "Nuovo contatto"}
                </p>
                <h3 className="text-lg font-semibold text-gray-900">
                  {isEditing
                    ? "Aggiorna responsabile"
                    : "Aggiungi responsabile"}
                </h3>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Chiudi modal contatto responsabile"
              >
                ✕
              </button>
            </div>
            <form className="p-6 space-y-5" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome
                  </label>
                  <input
                    type="text"
                    value={form.nome}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, nome: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                    maxLength={100}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cognome
                  </label>
                  <input
                    type="text"
                    value={form.cognome}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, cognome: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                    maxLength={100}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefono
                  </label>
                  <input
                    type="tel"
                    value={form.telefono}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, telefono: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    maxLength={30}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    maxLength={255}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  rows={4}
                  value={form.note}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, note: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  maxLength={2000}
                />
              </div>
              {user?.ruolo === "super_admin" && (
                <p className="text-xs text-gray-500">
                  Il contatto verrà associato alla congregazione attiva.
                </p>
              )}
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  disabled={submitting}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-60"
                  disabled={submitting}
                >
                  {submitting
                    ? "Salvataggio..."
                    : isEditing
                      ? "Salva modifiche"
                      : "Crea contatto"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContattoResponsabile;
